import React from 'react';
const { useState, useEffect, useCallback } = React;
import { cardSt, mutedSt, labelSt, badgeSt, warnSt, inputSt, fmtDate } from '../diagnostic/shared.jsx';
import { coachCall, Row, Visibility, useCoachingTools } from './shared.jsx';
import { getAuthHeaders } from '../../globals.js';
import { PlanView, PlanForm } from './SessionPanel.jsx';
import { prepLine, attemptLines, changesText, countsLines } from '../../sessionPrepView.js';

// Acompañamiento · preparación automática de sesiones (shared/sessionPrep.ts; acordado con Ale el 04-11-2026).
// «Ver preparación» abre lo guardado y nunca genera; editar, aprobar y descartar son las mismas acciones del editor
// de la sesión. Todo es un borrador privado de la coach: nada se comparte, asigna ni envía.

const smallBtn = { fontSize: 12, padding: '3px 10px' };
const TONE = { error: '#B4362C', warn: '#9A6212', accent: 'var(--globant-green)', ok: '#155C54', muted: 'var(--globant-muted)' };

/** The states of the preparations of some sessions (coach only). `status`: loading | ok | error | not_coach. */
export function useSessionPreps(sessionIds, enabled = true) {
  const key = (sessionIds || []).join(',');
  const [state, setState] = useState({ status: 'loading', data: null });
  const reload = useCallback(() => {
    if (!enabled || !key) { setState({ status: 'ok', data: { preps: {} } }); return Promise.resolve(); }
    setState(s => ({ ...s, status: s.data ? s.status : 'loading' }));
    return coachCall({ action: 'sessionPreps', sessionIds: key.split(',') })
      .then(data => setState({ status: 'ok', data }))
      .catch(e => setState(e.status === 403 || e.status === 401 ? { status: 'not_coach', data: null } : { status: 'error', data: null, error: e.message }));
  }, [key, enabled]);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
}

/** The line next to a session in Hoy, with «Ver preparación». A failed query is an error with «Reintentar». */
export function PrepStatus({ sessionId, preps, onOpen }) {
  if (!preps || preps.status === 'not_coach' || preps.status === 'loading') return null;
  if (preps.status === 'error') {
    const l = prepLine(null, { error: true });
    return <span role="alert" style={{ fontSize: 12, color: TONE.error, flexBasis: '100%' }}>{l.text}. <button type="button" className="action-btn" style={smallBtn} onClick={preps.reload}>Reintentar</button></span>;
  }
  const p = preps.data?.preps?.[sessionId];
  if (!p) return null;
  const l = prepLine(p);
  return (
    <span style={{ fontSize: 12, color: TONE[l.tone] || TONE.muted, flexBasis: '100%', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <span>{l.text}</span>
      {l.canOpen && <button type="button" className="action-btn" style={smallBtn} onClick={() => onOpen(sessionId)}>Ver preparación</button>}
    </span>
  );
}

/** Settings: off by default; she turns it on here. What it uses and what it never does, in a few lines. */
export function SessionPrepSettings({ onSaved }) {
  const [s, setS] = useState(null);
  const [counts, setCounts] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  const load = () => coachCall({ action: 'sessionPrepSettings' }).then(d => { setS(d.settings); setCounts(d.counts); setError(''); }).catch(e => setError(e.message));
  useEffect(() => { load(); }, []);
  const save = async (next) => {
    setBusy(true); setError(''); setSaved('');
    try { const d = await coachCall({ action: 'saveSessionPrepSettings', ...next }); setS(d.settings); setCounts(d.counts); setSaved('Guardado.'); onSaved?.(d.settings); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  if (error && !s) return <div role="alert" style={{ ...warnSt, padding: 10 }}>No se pudo leer la configuración: {error} <button type="button" className="action-btn" style={smallBtn} onClick={load}>Reintentar</button></div>;
  if (!s) return <div style={mutedSt}>Cargando configuración…</div>;
  return (
    <div style={{ ...cardSt, padding: 14 }}>
      <Row style={{ justifyContent: 'space-between' }}>
        <b>Preparación automática de sesiones</b>
        <span style={badgeSt(s.enabled)}>{s.enabled ? 'Encendida' : 'Apagada'}</span>
      </Row>
      <ul style={{ ...mutedSt, margin: '8px 0', paddingLeft: 18, display: 'grid', gap: 2 }}>
        <li>Prepara un borrador privado 24 h antes de cada sesión agendada (o en segundo plano al reservar, si falta menos).</li>
        <li>Usa lo mismo que «Proponer sesión»: programa, foco acordado, objetivos, resúmenes aprobados, compromisos, lo que la persona compartió y las herramientas activas de tu biblioteca. En una sesión grupal, solo lo compartido del equipo.</li>
        <li>Nunca usa tus notas privadas, transcripciones ni reflexiones privadas.</li>
        <li>No comparte, no asigna actividades, no envía mensajes, no toca Calendar ni marca sesiones. Si descartas una, no se vuelve a preparar sola.</li>
        <li>Hasta {s.monthlyLimit} plazas automáticas por mes; cada preparación reserva su plaza antes de llamar a la IA y la conserva en sus reintentos. Las que pides tú no cuentan.</li>
        <li>Una sola generación automática en curso por sesión. Si no se puede saber si la IA terminó, se detiene y te pide revisar: nunca repite sola una llamada incierta.</li>
      </ul>
      {counts && <div style={{ display: 'grid', gap: 2, fontSize: 12, margin: '4px 0 8px' }}>{countsLines(counts).map((t, i) => <div key={i} style={/límite/.test(t) && counts.overLimit ? { color: TONE.warn } : mutedSt}>{t}</div>)}</div>}
      <Row>
        <label style={{ fontSize: 13 }}><input type="checkbox" checked={s.individual} disabled={busy} onChange={e => save({ ...s, individual: e.target.checked })} /> Sesiones individuales</label>
        <label style={{ fontSize: 13 }}><input type="checkbox" checked={s.group} disabled={busy} onChange={e => save({ ...s, group: e.target.checked })} /> Sesiones grupales</label>
        <label style={{ fontSize: 13 }}>Límite mensual <input type="number" min={1} max={200} style={{ ...inputSt, width: 70 }} defaultValue={s.monthlyLimit} disabled={busy} onBlur={e => Number(e.target.value) !== s.monthlyLimit && save({ ...s, monthlyLimit: Number(e.target.value) })} /></label>
      </Row>
      <CoordinationCheck />
      <Row style={{ marginTop: 10 }}>
        <button type="button" className={`action-btn${s.enabled ? '' : ' btn-primary'}`} disabled={busy} onClick={() => save({ ...s, enabled: !s.enabled })}>{s.enabled ? 'Apagar' : 'Encender'}</button>
        {saved && <span style={mutedSt}>{saved}</span>}
        {error && <span role="alert" style={{ fontSize: 12, color: TONE.error }}>{error}</span>}
        {s.updatedAt && <span style={mutedSt}>Último cambio: {fmtDate(s.updatedAt, true)}</span>}
        {/* Solo en la demo: la pasada que en Oike corre cada hora */}
        {typeof window !== 'undefined' && typeof window.__demoPrepPass === 'function' && s.enabled && (
          <button type="button" className="action-btn" disabled={busy} onClick={async () => { setBusy(true); try { const r = await window.__demoPrepPass(); setSaved(`Pasada de prueba: ${r.started || 0} preparada(s).`); onSaved?.(s); } catch (e) { setError(e.message); } setBusy(false); }}>Probar la pasada programada (demo)</button>
        )}
      </Row>
    </div>
  );
}

/**
 * Comprobación técnica de la coordinación (solo la dueña; /api/coordination-check): deshabilitada por defecto, se
 * habilita para una sola ejecución y vuelve a quedar deshabilitada. Sin Airtable, sin IA y sin datos de clientes.
 */
function CoordinationCheck() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const call = async (body) => {
    const res = await fetch('/api/coordination-check', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { const e = new Error(data.error || `error ${res.status}`); e.status = res.status; throw e; }
    return data;
  };
  useEffect(() => { call({ action: 'status' }).then(() => setVisible(true)).catch(() => setVisible(false)); }, []);
  if (!visible) return null;
  const run = async () => {
    if (!window.confirm('¿Habilitar la comprobación técnica para una sola ejecución? Usa claves sintéticas en un espacio aislado de Netlify Blobs: sin Airtable, sin IA y sin datos de clientes. Después queda deshabilitada.')) return;
    setBusy(true); setError(''); setResult(null);
    try { const { armId } = await call({ action: 'arm', confirm: true }); setResult(await call({ action: 'run', armId })); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <details style={{ margin: '8px 0' }}>
      <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--globant-muted)' }}>Comprobación técnica de la coordinación (solo tú)</summary>
      <div style={{ ...mutedSt, margin: '6px 0' }}>Comprueba en el servicio real que «crear solo si no existe» deja un solo ganador, que las plazas no pasan del límite y que una respuesta perdida se recupera. Está deshabilitada: se habilita para una ejecución y vuelve a quedar deshabilitada. No toca sesiones reales.</div>
      <button type="button" className="action-btn" disabled={busy} onClick={run}>{busy ? 'Comprobando…' : 'Habilitar y ejecutar una vez'}</button>
      {error && <div role="alert" style={{ fontSize: 12, color: TONE.error, marginTop: 6 }}>No se pudo completar: {error}</div>}
      {result && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <b>{result.summary.passed} de {result.summary.total} comprobaciones correctas</b> · {result.summary.ms} ms · ejecución {result.summary.runId.slice(0, 8)} · queda deshabilitada
          {result.checks.map(c => <div key={c.name} style={{ color: c.ok ? TONE.ok : TONE.error }}>{c.ok ? '✓' : '✗'} {c.what} <span style={mutedSt}>{JSON.stringify(c.detail)}</span></div>)}
        </div>
      )}
    </details>
  );
}

/** «Ver preparación»: what is saved for one session, never generated here unless she asks («Preparar de nuevo»). */
export function SessionPrepDialog({ sessionId, onClose, onChanged }) {
  const tools = useCoachingTools();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const [showPrev, setShowPrev] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const load = useCallback(() => coachCall({ action: 'sessionPrep', sessionId }).then(x => { setD(x); setError(''); }).catch(e => setError(e.message || 'No se pudo consultar la preparación.')), [sessionId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.state?.state !== 'running') return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [d?.state?.state, load]);
  useEffect(() => { const k = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);

  const act = async (body) => {
    setBusy(true); setError('');
    try { await coachCall(body); setEdit(null); await load(); onChanged?.(); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  const prepareAgain = async () => {
    const out = d?.output;
    const edited = out?.status === 'draft' && out.editedAt;
    if (edited && !window.confirm('Editaste este borrador. ¿Preparar uno nuevo? El editado se conserva como versión anterior y tu plan aprobado no cambia.')) return;
    await act({ action: 'proposeSession', processId: d.session.processId, sessionId, ...(edited ? { confirmEdited: true } : {}) });
  };

  const out = d?.output;
  const st = d?.state?.state;
  const line = d ? prepLine(d.state) : null;
  const auto = out?.inputs?.trigger === 'auto' || d?.state?.trigger === 'auto';
  const attempts = out?.inputs?.auto?.attempts || d?.state?.attempts || [];

  return (
    <div role="dialog" aria-modal="true" aria-label="Preparación de la sesión" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(30,42,44,0.25)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ background: 'var(--globant-bg, var(--globant-card))', width: 'min(720px, 100%)', height: '100%', overflowY: 'auto', padding: 16, boxSizing: 'border-box' }}>
        <Row style={{ justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={labelSt}>Preparación de la sesión</div>
            <b>{d?.session?.title || 'Sesión'}</b>{d?.session?.startsAt && <span style={mutedSt}> · {fmtDate(d.session.startsAt, true)}</span>}
          </div>
          <Row><Visibility kind="coach_private" label="Solo tú: nada se envía ni se publica" /><button type="button" className="action-btn" onClick={onClose}>Cerrar</button></Row>
        </Row>

        {error && <div role="alert" style={{ ...warnSt, padding: 10 }}>{error} <button type="button" className="action-btn" style={smallBtn} onClick={load}>Reintentar</button></div>}
        {!d && !error && <div style={mutedSt}>Cargando lo guardado…</div>}

        {d && <>
          <div style={{ fontSize: 13, color: TONE[line.tone] || TONE.muted, marginBottom: 8 }}>{line.text}</div>

          {['off', 'out_of_scope'].includes(st) && <SessionPrepSettings onSaved={() => { load(); onChanged?.(); }} />}
          {st === 'limit' && <div style={{ ...mutedSt, marginBottom: 8 }}>Puedes subir el límite en la configuración o preparar esta sesión tú misma (no cuenta para el límite).</div>}
          {st === 'scheduled' && <div style={{ ...mutedSt, marginBottom: 8 }}>Todavía no hay nada preparado. Puedes esperar o prepararla tú ahora (no cuenta para el límite).</div>}

          {out && ['draft', 'approved'].includes(out.status) && (
            <div style={{ ...cardSt, padding: 14 }}>
              <Row style={{ marginBottom: 8 }}>
                <span style={badgeSt(out.status === 'approved')}>{out.status === 'approved' ? 'Aprobada: es el plan de la sesión' : 'Borrador'}</span>
                <span style={mutedSt}>{auto ? 'Automática' : 'Pedida por ti'} · v{out.version}{out.finishedAt ? ` · ${fmtDate(out.finishedAt, true)}` : ''}{out.editedAt ? ` · editada ${fmtDate(out.editedAt, true)}` : ''}</span>
              </Row>
              {changesText(d.changes).map((t, i) => <div key={i} style={{ ...warnSt, padding: 8, marginBottom: 8, fontSize: 13 }}>{t}</div>)}
              {edit ? <PlanForm value={edit} onChange={setEdit} /> : <PlanView plan={out.content} tools={tools} />}
              <Row style={{ marginTop: 10 }}>
                {out.status === 'draft' && (edit ? <>
                  <button type="button" className="action-btn btn-primary" disabled={busy} onClick={() => act({ action: 'editAiOutput', outputId: out.id, content: edit })}>Guardar cambios</button>
                  <button type="button" className="action-btn" onClick={() => setEdit(null)}>Cancelar</button>
                </> : <>
                  <button type="button" className="action-btn" onClick={() => setEdit(out.content)}>✏️ Editar</button>
                  <button type="button" className="action-btn btn-primary" disabled={busy} onClick={() => act({ action: 'approveAiOutput', outputId: out.id })}>✓ Aprobar como plan</button>
                  <button type="button" className="action-btn" disabled={busy} onClick={() => act({ action: 'discardAiOutput', outputId: out.id })}>Descartar</button>
                </>)}
                {!edit && <button type="button" className="action-btn" disabled={busy} onClick={prepareAgain}>Preparar de nuevo</button>}
              </Row>
              {d.previous && (
                <div style={{ marginTop: 10 }}>
                  <button type="button" className="action-btn" style={smallBtn} onClick={() => setShowPrev(v => !v)}>{showPrev ? 'Ocultar la versión anterior' : 'Ver la versión anterior'}</button>
                  {showPrev && <div style={{ marginTop: 8, opacity: 0.85 }}><div style={mutedSt}>v{d.previous.version}{d.previous.editedAt ? ` · editada ${fmtDate(d.previous.editedAt, true)}` : ''} (solo lectura)</div><PlanView plan={d.previous.content} tools={tools} /></div>}
                </div>
              )}
            </div>
          )}

          {st === 'running' && <div style={mutedSt}>Preparando… Se actualiza solo; puedes cerrar esta ventana.</div>}
          {st === 'failed' && (
            <div style={{ ...warnSt, padding: 10 }}>
              No se pudo preparar ({d.state.error}).
              {d.state.autoRetry ? ' Habrá un reintento automático; no hace falta hacer nada.' : ''}
              <Row style={{ marginTop: 8 }}>
                <button type="button" className="action-btn btn-primary" disabled={busy} onClick={() => { if (window.confirm('¿Reintentar? Usa la misma plaza mensual, pero puede consumir IA adicional.')) act({ action: 'prepRetry', outputId: d.state.outputId }); }}>Reintentar</button>
                <button type="button" className="action-btn" disabled={busy} onClick={() => act({ action: 'discardAiOutput', outputId: d.state.outputId })}>Descartar</button>
              </Row>
            </div>
          )}
          {st === 'discarded' && <div style={{ ...mutedSt, marginBottom: 8 }}>No se vuelve a preparar sola. Si la quieres, prepárala tú.</div>}
          {st === 'uncertain' && (
            <div style={{ ...warnSt, padding: 10 }}>
              No sabemos si la IA terminó: la llamada no respondió o se interrumpió. No se repite sola.
              <Row style={{ marginTop: 8 }}>
                <button type="button" className="action-btn" disabled={busy} onClick={load}>Comprobar de nuevo</button>
                <button type="button" className="action-btn btn-primary" disabled={busy} onClick={() => { if (window.confirm('¿Reintentar? Puede consumir IA otra vez (la llamada anterior pudo haber terminado). Si su respuesta llega tarde, se descarta.')) act({ action: 'prepRetry', outputId: d.state.outputId }); }}>Reintentar</button>
                <button type="button" className="action-btn" disabled={busy} onClick={() => act({ action: 'discardAiOutput', outputId: d.state.outputId })}>Descartar</button>
              </Row>
            </div>
          )}
          {st === 'uncertain_saving' && (
            <div style={{ ...warnSt, padding: 10 }}>
              La IA terminó y su resultado puede estar guardándose todavía. Para no competir con esa escritura, esta preparación no se reintenta ni se descarta hasta comprobarla.
              <Row style={{ marginTop: 8 }}>
                <button type="button" className="action-btn btn-primary" disabled={busy} onClick={load}>Comprobar de nuevo</button>
                <button type="button" className="action-btn" disabled={busy} onClick={prepareAgain}>Prepararla yo ahora</button>
              </Row>
              <div style={{ ...mutedSt, marginTop: 6 }}>Prepararla tú crea una propuesta aparte (sin notas privadas ni transcripciones) y consume IA.</div>
            </div>
          )}
          {st === 'coordination_error' && <div role="alert" style={{ ...warnSt, padding: 10 }}>No se pudo comprobar el estado de esta preparación. No significa que no exista. <button type="button" className="action-btn" style={smallBtn} onClick={load}>Reintentar la consulta</button></div>}
          {['discarded', 'limit', 'scheduled', 'pending', 'off', 'out_of_scope'].includes(st) && !(out && ['draft', 'approved', 'running'].includes(out.status)) && (
            <Row style={{ marginTop: 6 }}><button type="button" className="action-btn" disabled={busy} onClick={prepareAgain}>{st === 'discarded' ? 'Preparar de nuevo' : 'Prepararla ahora'}</button><span style={mutedSt}>Sin notas privadas ni transcripciones; para incluirlas, usa la propuesta dentro de la sesión.</span></Row>
          )}

          {attempts.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <span style={labelSt}>Intentos</span>
              {attemptLines(attempts).map((t, i) => <div key={i} style={mutedSt}>{t}</div>)}
            </div>
          )}

          {!['off', 'out_of_scope'].includes(st) && (
            <div style={{ marginTop: 14 }}>
              <button type="button" className="action-btn" style={smallBtn} onClick={() => setSettingsOpen(v => !v)}>{settingsOpen ? 'Ocultar configuración' : 'Configuración de la preparación automática'}</button>
              {settingsOpen && <div style={{ marginTop: 8 }}><SessionPrepSettings onSaved={() => { load(); onChanged?.(); }} /></div>}
            </div>
          )}
        </>}
      </div>
    </div>
  );
}
